/**
 * 공지사항 상세 페이지 (V86)
 *
 * V86 UI 리디자인:
 * - 제목 영역: 연한 배경색, 작성자/작성일 우측 상단
 * - 첨부파일: 제목 영역 바로 아래
 * - 본문: 구분선 이후, max-width 700px, line-height 1.75
 */

import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pin,
  Download,
  Edit2,
  Trash2,
  FileText,
  Image,
  FileSpreadsheet,
  File,
} from 'lucide-react';
import {
  useAnnouncementDetail,
  useAnnouncementMutations,
  AnnouncementAttachment,
  markAnnouncementRead,
} from '../../shared/hooks/useAnnouncements';
import { popupDetailKey, useHoldPopups } from '../../shared/hooks/popupHold';
import RichContentViewer from '../../shared/components/RichContentViewer';

export function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { announcement, canEdit, isLoading, error } = useAnnouncementDetail(id || null);
  const { deleteAnnouncement, isSubmitting } = useAnnouncementMutations();

  // V70G8C: 상세를 보는 동안 다음 팝업 표시 보류 (목록 등으로 나가면 이어서 표시)
  // V70G8E: 팝업 '상세 보기'/딥링크로 잡아 둔 대기 보류를 이 상세가 넘겨받음
  useHoldPopups(true, id ? popupDetailKey('erp', id) : null);

  // V70G8C: 상세가 실제로 열렸을 때만 읽음 기록 (공지당 1회). 성공 시 LNB 배지 즉시 갱신.
  const loadedId = announcement && !isLoading && !error ? announcement.id : null;
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedId || markedRef.current === loadedId) return;
    markedRef.current = loadedId;
    void markAnnouncementRead(loadedId);
  }, [loadedId]);

  // 날짜 포맷
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\. /g, '.').replace(/\.$/, '');
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 파일 아이콘
  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <Image size={14} />;
    if (contentType.includes('spreadsheet') || contentType.includes('excel'))
      return <FileSpreadsheet size={14} />;
    if (contentType.includes('pdf')) return <FileText size={14} />;
    return <File size={14} />;
  };

  // 수정
  const handleEdit = () => {
    navigate(`/announcements/${id}/edit`);
  };

  // 삭제
  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('이 공지사항을 삭제하시겠습니까?')) return;

    try {
      await deleteAnnouncement(id);
      navigate('/announcements');
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 첨부파일 다운로드
  const handleDownload = async (attachment: AnnouncementAttachment) => {
    // V300.1: TXT 파일은 Blob 다운로드로 강제 (Firebase Storage download URL은
    // response-content-disposition 미지원, cross-origin이라 <a download> 무시)
    const ext = attachment.name.split('.').pop()?.toLowerCase();
    if (ext === 'txt') {
      try {
        const res = await fetch(attachment.url);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = attachment.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch {
        // fallback: fetch 실패 시 기존 방식
        window.open(attachment.url, '_blank');
      }
      return;
    }

    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.target = '_blank';
    link.click();
  };

  // 목록으로
  const handleBack = () => {
    navigate('/announcements');
  };

  // 로딩
  if (isLoading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div
          style={{
            padding: '80px 24px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '14px',
          }}
        >
          불러오는 중...
        </div>
      </div>
    );
  }

  // 에러
  if (error || !announcement) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div
          style={{
            padding: '80px 24px',
            textAlign: 'center',
            color: '#dc2626',
            fontSize: '14px',
          }}
        >
          {error || '공지사항을 찾을 수 없습니다.'}
        </div>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={handleBack}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  const hasAttachments = announcement.attachments && announcement.attachments.length > 0;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* 상단 네비게이션 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <button
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '0',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            fontSize: '13px',
          }}
        >
          <ArrowLeft size={15} />
          목록
        </button>

        {canEdit && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleEdit}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: '#4b5563',
              }}
            >
              <Edit2 size={13} />
              수정
            </button>
            <button
              onClick={handleDelete}
              disabled={isSubmitting}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid #fecaca',
                borderRadius: '5px',
                cursor: isSubmitting ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: '#dc2626',
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              <Trash2 size={13} />
              삭제
            </button>
          </div>
        )}
      </div>

      {/* 본문 카드 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        {/* ========================================
            제목 영역 (Header) - 연한 배경
            ======================================== */}
        <div
          style={{
            background: '#f9fafb',
            padding: '24px 28px 20px',
          }}
        >
          {/* 제목 행: [중요 배지 + 제목] 좌측, [작성자/날짜] 우측 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            {/* 좌측: 중요 배지 + 제목 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                minWidth: 0,
              }}
            >
              {announcement.isPinned && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    padding: '3px 8px',
                    background: '#fef2f2',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#dc2626',
                    flexShrink: 0,
                  }}
                >
                  <Pin size={11} fill="#dc2626" />
                  중요
                </span>
              )}
              <h1
                style={{
                  fontSize: '19px',
                  fontWeight: 600,
                  margin: 0,
                  color: '#1f2937',
                  lineHeight: 1.45,
                  letterSpacing: '-0.01em',
                }}
              >
                {announcement.title}
              </h1>
            </div>

            {/* 우측: 작성자 · 작성일 */}
            <div
              style={{
                fontSize: '12px',
                color: '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexShrink: 0,
              }}
            >
              <span style={{ color: '#6b7280' }}>{announcement.createdByName}</span>
              <span>·</span>
              <span>{formatDate(announcement.createdAt)}</span>
              {announcement.updatedAt !== announcement.createdAt && (
                <>
                  <span>·</span>
                  <span style={{ fontSize: '11px' }}>수정됨</span>
                </>
              )}
            </div>
          </div>

          {/* 첨부파일 - 제목 바로 아래 */}
          {hasAttachments && (
            <div
              style={{
                marginTop: '14px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              {announcement.attachments.map((att) => (
                <button
                  key={att.id}
                  onClick={() => handleDownload(att)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#4b5563',
                    maxWidth: '240px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <span style={{ color: '#9ca3af', flexShrink: 0 }}>
                    {getFileIcon(att.contentType)}
                  </span>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {att.name}
                  </span>
                  <span style={{ fontSize: '10px', color: '#9ca3af', flexShrink: 0 }}>
                    {formatFileSize(att.size)}
                  </span>
                  <Download size={12} style={{ color: '#9ca3af', flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ========================================
            구분선
            ======================================== */}
        <div style={{ borderTop: '1px solid #e5e7eb' }} />

        {/* ========================================
            본문 영역 (Content)
            ======================================== */}
        <div style={{ padding: '28px' }}>
          {/* V279: HTML(리치 에디터) / plain(legacy) 자동 분기 + DOMPurify 정화 */}
          <RichContentViewer
            content={announcement.content}
            format={announcement.contentFormat || 'plain'}
            style={{ maxWidth: '700px' }}
            // V70G8H: 본문 내부 링크는 SPA 이동 (대상 라우트 가드가 권한 검사)
            onInternalLink={(path) => navigate(path)}
          />
        </div>
      </div>
    </div>
  );
}

export default AnnouncementDetailPage;
